import React, { useMemo, useState } from "react";
import { Form, Formik, FormikConfig, useFormikContext } from "formik";
import { Scrollbars } from "react-custom-scrollbars-2";
import { v4 as uuid } from "uuid";
import {
    TransactionRule,
    RuleActionType,
    RuleGroupType,
    TransferRuleDirection,
    RuleConditionType,
} from "@common/types/transactionRule";
import { useCategoriesTree } from "@hooks/useCategoriesTree";
import classNames from "classnames";
import { Taxonomy } from "@common/categories";
import { NewTransactionRuleDefinition } from "@components/transactionRules/TransactionRuleCreation/TransactionRuleCreationDrawer";
import { useEntities } from "@hooks/useEntities";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { submitHelper } from "@helpers/form";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { useWorkspaceRuleGroupsList } from "@api/rules.api";
import {
    FormRuleConditionType,
    FormRuleActionType,
} from "../TransactionRuleForm/constants";
import {
    conditionModelToFormRow,
    getDefaultFormRow,
    modelToFormActions,
} from "../TransactionRuleForm/modelToForm.helpers";
import {
    conditionFormRowToModel,
    formActionsToModel,
} from "../TransactionRuleForm/formToModel.helpers";
import { ApplyRuleToExistingTransactions } from "../TransactionRuleForm/ApplyRuleToExistingTransactions";
import { TransactionRuleSubmitData } from "../TransactionRuleForm/RuleForm";
import { RuleGroupForm } from "../TransactionRuleForm/RuleGroupForm/RuleGroupForm";
import styles from "./TransferRuleForm.module.scss";
import { TransferRuleTransactionsPreview } from "./TransferRuleTransactionsPreview/TransferRuleTransactionsPreview";
import { defaultCategoryTaxonomy, TransferRuleFormSchema } from "./constants";
import { TransferRuleConditionsForm } from "./TransferRuleConditionsForm/TransferRuleConditionsForm";
import { ExcludeMatchedTransactionsCheckbox } from "./TransferRuleConditionsForm/ExcludeMatchedTransactions";
import { TransferRuleActionsForm } from "./TransferRuleActionsForm/TransferRuleActionsForm";
import { DateProximityConditionForm } from "./TransferRuleConditionsForm/DateProximityConditionForm";
import { MatchingModeSelect } from "./TransferRuleActionsForm/MatchingModeSelect";

interface Props {
    rule?: TransactionRule;
    onSubmit: (data: TransactionRuleSubmitData) => Promise<void>;
    isSubmitting: boolean;
    submitText: string;
    newRuleDefinition?: NewTransactionRuleDefinition;
    onTypeChange?: (type: RuleGroupType) => void;
}

export const TransferRuleForm: React.FC<Props> = (props) => {
    const ruleGroupsList = useWorkspaceRuleGroupsList();

    if (!ruleGroupsList.data) {
        return null;
    }

    return <TransferRuleFormInner {...props} />;
};

const TransferRuleFormInner: React.FC<Props> = ({
    rule,
    onSubmit,
    isSubmitting,
    submitText,
    newRuleDefinition,
    onTypeChange,
}) => {
    const [expanded, setExpanded] = useState(false);

    const businessEntities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const { enabled: isGlFirst } = useIsGlFirst();
    const categoriesById = useCategoriesById();
    const ruleGroupsList = useWorkspaceRuleGroupsList();

    const initialData = rule ?? newRuleDefinition;
    const categories = useCategoriesTree();

    const initialValues = useMemo(() => {
        const conditions = initialData?.conditions
            ?.map((c) =>
                conditionModelToFormRow({
                    conditionModel: c,
                    categoriesById,
                }),
            )
            .filter((v) => v !== undefined);

        let initialGroupId = initialData?.groupId;

        const defaultCategories = categories
            .filter((c) => defaultCategoryTaxonomy.includes(c.taxonomy))
            .filter(({ id }) => id);

        const deaultCategoriesIds = defaultCategories.map(({ id }) => id);
        const defaultCategoryFrom = defaultCategories.find(
            (c) => c.taxonomy === Taxonomy.transfer,
        );
        const defaultCategoryTo = defaultCategories.find(
            (c) => c.taxonomy === Taxonomy.transfer,
        );

        // GL-first workspaces classify by general ledger, so new rules are
        // not seeded with category conditions.
        const buildDefaultCategoryConditions = (
            transferDirection: TransferRuleDirection,
        ) =>
            isGlFirst
                ? []
                : [
                      {
                          id: uuid(),
                          ...getDefaultFormRow(),
                          conditionType: FormRuleConditionType.CATEGORY,
                          categoryIds: deaultCategoriesIds,
                          transferDirection,
                      },
                  ];

        const initialConditions = conditions?.length
            ? conditions
            : [
                  {
                      id: uuid(),
                      ...getDefaultFormRow(),
                      conditionType: FormRuleConditionType.ENTITY,
                      entityValue: businessEntities.map((e) => e.id),
                      transferDirection: TransferRuleDirection.FROM,
                  },
                  ...buildDefaultCategoryConditions(TransferRuleDirection.FROM),
                  {
                      id: uuid(),
                      ...getDefaultFormRow(),
                      conditionType: FormRuleConditionType.ACCOUNT,
                      accountValue: [],
                      transferDirection: TransferRuleDirection.FROM,
                  },
                  {
                      id: uuid(),
                      ...getDefaultFormRow(),
                      conditionType: FormRuleConditionType.ENTITY,
                      entityValue: businessEntities.map((e) => e.id),
                      transferDirection: TransferRuleDirection.TO,
                  },
                  ...buildDefaultCategoryConditions(TransferRuleDirection.TO),
                  {
                      id: uuid(),
                      ...getDefaultFormRow(),
                      conditionType: FormRuleConditionType.ACCOUNT,
                      accountValue: [],
                      transferDirection: TransferRuleDirection.TO,
                  },
                  {
                      id: uuid(),
                      ...getDefaultFormRow(),
                      conditionType: FormRuleConditionType.DATE_PROXIMITY,
                      dateProximity: 4,
                  },
              ];

        const hasDisallowAction = initialData?.actions?.some(
            (a) =>
                a.actionType === RuleActionType.TRANSFER_MATCHING &&
                a.value === "disallow",
        );

        // GL-first workspaces classify by general ledger, so new rules
        // default to an optional account assignment instead of category
        // actions.
        const getDefaultActions = () =>
            isGlFirst
                ? {
                      from: [
                          {
                              id: uuid(),
                              actionType: FormRuleActionType.ACCOUNT_ID,
                              transferDirection: TransferRuleDirection.FROM,
                          },
                      ],
                      to: [
                          {
                              id: uuid(),
                              actionType: FormRuleActionType.ACCOUNT_ID,
                              transferDirection: TransferRuleDirection.TO,
                          },
                      ],
                  }
                : {
                      from: [
                          {
                              id: uuid(),
                              actionType: FormRuleActionType.CATEGORY,
                              value: { categoryId: defaultCategoryFrom?.id },
                              transferDirection: TransferRuleDirection.FROM,
                          },
                      ],
                      to: [
                          {
                              id: uuid(),
                              actionType: FormRuleActionType.CATEGORY,
                              value: { categoryId: defaultCategoryTo?.id },
                              transferDirection: TransferRuleDirection.TO,
                          },
                      ],
                  };

        const initialActions = initialData?.actions
            ? {
                  from: modelToFormActions(
                      initialData.actions.filter(
                          (action) =>
                              action.transferDirection ===
                              TransferRuleDirection.FROM,
                      ),
                  ),
                  to: modelToFormActions(
                      initialData.actions.filter(
                          (action) =>
                              action.transferDirection ===
                              TransferRuleDirection.TO,
                      ),
                  ),
              }
            : getDefaultActions();

        const initialGroup = ruleGroupsList.data?.find((group) => {
            if (initialGroupId) {
                return group.id === initialGroupId;
            }

            return group.isDefault && group.type === RuleGroupType.TRANSFER;
        });

        initialGroupId ??= initialGroup?.id;

        return {
            groupId: initialGroupId ?? "",
            conditions: {
                from: initialConditions.filter(
                    (c) => c.transferDirection === TransferRuleDirection.FROM,
                ),
                to: initialConditions.filter(
                    (c) => c.transferDirection === TransferRuleDirection.TO,
                ),
                dateProximity: initialConditions.find(
                    (c) =>
                        c.conditionType ===
                        FormRuleConditionType.DATE_PROXIMITY,
                )?.dateProximity,
            },
            actions: initialActions,
            matchingMode: hasDisallowAction
                ? ("disallow" as const)
                : ("allow" as const),
            applyToExistingTransactions:
                newRuleDefinition?.applyToExistingTransactions ?? true,
            excludedTransactionsIds: [],
            includedTransactionsIds: [],
            initialGroup,
            excludeMatchedTransfers:
                newRuleDefinition?.excludeMatchedTransfers ?? true,
        };
    }, [
        initialData,
        newRuleDefinition,
        ruleGroupsList.data,
        businessEntities,
        categoriesById,
        categories,
        isGlFirst,
    ]);

    const form: FormikConfig<TransferRuleFormSchema> = {
        initialValues,
        validateOnChange: false,
        onSubmit: submitHelper({
            handler: async ({
                groupId,
                actions,
                conditions,
                matchingMode,
                applyToExistingTransactions,
                excludedTransactionsIds,
                includedTransactionsIds,
                excludeMatchedTransfers,
            }) => {
                const conditionsToSend = [
                    ...conditions.from
                        .map(conditionFormRowToModel)
                        .filter((v) => v !== undefined),
                    ...conditions.to
                        .map(conditionFormRowToModel)
                        .filter((v) => v !== undefined),
                ];

                if (conditions.dateProximity) {
                    conditionsToSend.push({
                        conditionType: RuleConditionType.DATE_PROXIMITY,
                        value: conditions.dateProximity,
                        transferDirection: null,
                    });
                }

                let allActions;
                if (matchingMode === "disallow") {
                    const disallowAction: {
                        actionType: RuleActionType;
                        value: string | number | string[];
                        order: number;
                        transferDirection: null;
                    } = {
                        actionType: RuleActionType.TRANSFER_MATCHING,
                        value: "disallow",
                        order: 0,
                        transferDirection: null,
                    };
                    allActions = [disallowAction];
                } else {
                    const fromActions = actions.from.map((action) => ({
                        ...action,
                        transferDirection: TransferRuleDirection.FROM,
                    }));
                    const toActions = actions.to.map((action) => ({
                        ...action,
                        transferDirection: TransferRuleDirection.TO,
                    }));
                    allActions = [
                        ...formActionsToModel(fromActions),
                        ...formActionsToModel(toActions),
                    ];
                }

                await onSubmit({
                    rule: {
                        actions: allActions,
                        conditions: conditionsToSend,
                        groupId,
                    },
                    applyToExistingTransactions,
                    excludedTransactionsIds,
                    includedTransactionsIds,
                    excludeMatchedTransfers,
                });
            },
            loading: isSubmitting,
        }),
    };

    return (
        <Formik {...form}>
            <TransferRuleFormBody
                rule={rule}
                isSubmitting={isSubmitting}
                submitText={submitText}
                expanded={expanded}
                onExpand={() => setExpanded(!expanded)}
                onTypeChange={onTypeChange}
            />
        </Formik>
    );
};

const TransferRuleFormBody: React.FC<{
    rule?: TransactionRule;
    isSubmitting: boolean;
    submitText: string;
    expanded: boolean;
    onExpand: () => void;
    onTypeChange?: (type: RuleGroupType) => void;
}> = ({ rule, isSubmitting, submitText, expanded, onExpand, onTypeChange }) => {
    const { values } = useFormikContext<TransferRuleFormSchema>();
    const isDisallowMode = values.matchingMode === "disallow";

    return (
        <Form className={styles.form}>
            <div
                className={classNames(styles.container, {
                    [styles.expanded]: expanded,
                })}
            >
                <Scrollbars>
                    <div className={styles.formContainer}>
                        <RuleGroupForm
                            groupType={RuleGroupType.TRANSFER}
                            onTypeChange={onTypeChange}
                        />
                        <MatchingModeSelect />
                        <TransferRuleConditionsForm
                            transferDirection={TransferRuleDirection.FROM}
                        />
                        <TransferRuleActionsForm
                            transferDirection={TransferRuleDirection.FROM}
                            disabled={isDisallowMode}
                        />
                        <TransferRuleConditionsForm
                            transferDirection={TransferRuleDirection.TO}
                        />
                        <TransferRuleActionsForm
                            transferDirection={TransferRuleDirection.TO}
                            disabled={isDisallowMode}
                        />
                    </div>
                </Scrollbars>
                <TransferRuleTransactionsPreview
                    rule={rule}
                    onExpand={onExpand}
                    expanded={expanded}
                />
            </div>

            <footer className={styles.footer}>
                <div className={styles.selectorsContainer}>
                    <DateProximityConditionForm />
                    <ApplyRuleToExistingTransactions label="Do not apply to historical transactions" />
                    <ExcludeMatchedTransactionsCheckbox />
                </div>
                <ButtonWithLoader
                    variant="primary"
                    size="md"
                    type="submit"
                    loading={isSubmitting}
                >
                    {submitText}
                </ButtonWithLoader>
            </footer>
        </Form>
    );
};
